/* ==========================================================================
   Picking places

   This was a catalog of twenty-six Rockland and Brooklyn neighbourhoods, the
   same list for everyone, in three separate copies: the home's filter, the
   cleaner's filter, and the cleaner's own profile. A home in Lakewood was
   offered somebody else's county and none of its own.

   It asks the server now, which works the list out from the postal code the
   person already gave us against every town in the United States. One copy,
   because three drifting copies of a list is how two of them end up wrong.

   The old catalog is still the fallback, and deliberately: somebody who has
   not set a postal code would otherwise get an empty picker, and every
   account that existed before this shipped is in Rockland or Brooklyn
   anyway.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { Pick } from './ui';
import { api } from '../api';
import { areasNear } from '../lib/domain';
import { t, tf } from '../lib/i18n';

/* state is shown on search results: there is a Brooklyn in New York and
   another in Connecticut, and a bare name cannot tell them apart. */
interface Town { id: string; town: string; state?: string; miles: number }

export function TownPicks({
  zip, anchors = [], chosen, onFlip, within = 25, shown = 12,
}: {
  /** Where this person is. With none, the old catalog stands in. */
  zip: string;
  /** Legacy area ids to sort the fallback catalog around. */
  anchors?: string[];
  chosen: string[];
  onFlip: (id: string) => void;
  within?: number;
  shown?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const [towns, setTowns] = useState<Town[] | null>(null);
  /* Find a town by name. The list below is the nearest forty, which in a dense
     area is ten miles' worth; anything further, however far she travels, is
     only reachable by typing it. */
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Town[] | null>(null);

  useEffect(() => {
    const name = q.trim();
    if (!zip || name.length < 2) { setFound(null); return; }
    let alive = true;
    api.placesNear(zip, within, name)
      .then((r) => { if (alive) setFound(r.towns); })
      .catch(() => { if (alive) setFound([]); });
    return () => { alive = false; };
  }, [zip, within, q]);

  useEffect(() => {
    if (!zip) { setTowns(null); return; }
    let alive = true;
    api.placesNear(zip, within)
      .then((r) => { if (alive) setTowns(r.towns); })
      .catch(() => { if (alive) setTowns(null); });
    /* A stale answer landing late would replace a newer one: somebody typing
       a postal code fires a lookup per keystroke. */
    return () => { alive = false; };
  }, [zip, within]);

  if (towns && towns.length) {
    const near = towns.slice(0, shown);
    const rest = towns.slice(shown);
    /* Anything already ticked stays on screen however far away it is. A
       selection you cannot see is one you cannot undo. */
    const held = chosen
      .filter((c) => !near.some((x) => x.id === c))
      .map((c) => ({ id: c, town: c.split(',')[0], miles: -1 }));
    const list = showAll ? [...near, ...rest, ...held] : [...near, ...held];

    return (
      <>
        <input
          className="input grow"
          id="town-search"
          placeholder={tf('tx_find_town', { n: within })}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {found !== null && (
          found.length ? (
            <div className="chips">
              {found.map((x) => (
                <Pick key={'f' + x.id} label={x.town + ', ' + x.state + ' · ' + Math.round(x.miles) + ' mi'}
                  on={chosen.includes(x.id)} onClick={() => onFlip(x.id)} />
              ))}
            </div>
          ) : (
            <span className="hint">
              {tf('tx_no_town', { n: within })}
            </span>
          )
        )}
        <div className="chips">
          {list.map((x) => (
            <Pick key={x.id} label={x.town} on={chosen.includes(x.id)}
              onClick={() => onFlip(x.id)} />
          ))}
        </div>
        {rest.length > 0 && (
          <button type="button" className="btn quiet sm" onClick={() => setShowAll(!showAll)}>
            {showAll ? t('sx_nearonly') : t('sx_allareas') + ' (' + rest.length + ')'}
          </button>
        )}
      </>
    );
  }

  const { near, far } = areasNear(anchors, { keep: chosen });
  return (
    <>
      <div className="chips">
        {(showAll ? [...near, ...far] : near).map((a) => (
          <Pick key={a.id} label={a.label} on={chosen.includes(a.id)}
            onClick={() => onFlip(a.id)} />
        ))}
      </div>
      {far.length > 0 && (
        <button type="button" className="btn quiet sm" onClick={() => setShowAll(!showAll)}>
          {showAll ? t('sx_nearonly') : t('sx_allareas') + ' (' + far.length + ')'}
        </button>
      )}
    </>
  );
}
